import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  BeakerIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  ShareIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ExtractionFreshness } from '@avisoma/shared';
import { FaSpinner } from 'react-icons/fa';
import backend from '../../services/backend';
import { PageHeader } from '../../components/PageHeader';
import { entityViewTitle } from '../../components/ID';
import { Button } from '../../catalyst/button';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../../catalyst/dropdown';
import { useToast } from '../../components/Toast';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DebugObject } from '../../components/DebugObject';
import AuthGuard from '../../components/AuthGuard';
import { useAuth } from '../../hooks/useAuth';
import { ExtractionFreshnessBadge } from '../../components/ExtractionFreshnessBadge';
import { formatRecoverProjectToast } from './recoverProjectToast';
import { useFinalizeBusy, useFinalizeProject } from './useFinalizeProject';
import { useSupervisorFindings } from './useSupervisorFindings';

interface HeaderProps {
  documentIds?: string[];
  debugObject?: unknown;
  extractionFreshness?: ExtractionFreshness;
}

type ReclassifyScope = 'project' | 'all';

export function Header({ documentIds, debugObject, extractionFreshness }: HeaderProps) {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirmScope, setConfirmScope] = useState<ReclassifyScope | null>(null);

  const finalizeProjectMutation = useFinalizeProject(id);
  // Shared with the Corrections tab's Finalize: no second finalize while the queued one still runs.
  // Only the Finalize item in the admin + curator Actions dropdown reads it. This observer
  // keeps the finalize poll alive on every tab.
  const canRunActions = useAuth().hasAccess(['admin', 'curator']);
  const { data: findings } = useSupervisorFindings(canRunActions ? id : undefined);
  const finalizeBusy = useFinalizeBusy(id, findings?.finalizedAt);

  // One bulk request per action — the server enqueues a task per document, so the
  // browser no longer fans out one HTTP retry per document (DR-2602).
  const reclassifyMutation = useMutation({
    mutationFn: async (scope: ReclassifyScope) => {
      if (!documentIds || (scope === 'project' && !id)) {
        return;
      }
      return backend.bulkRetryDocuments(
        documentIds,
        'classification',
        scope === 'project' ? { projectId: id } : undefined,
      );
    },
    onSuccess: (result, scope) => {
      // Nothing was sent (no documents, or no project id for the scoped action).
      if (!result) {
        return;
      }
      // The server answers once the per-document tasks are queued, not once they ran.
      const { enqueued } = result.data;
      const documentLabel = `${enqueued} document${enqueued === 1 ? '' : 's'}`;
      toast.success(
        scope === 'all'
          ? `Reclassification queued for ${documentLabel}`
          : `Reclassification queued for ${documentLabel} in this project`,
      );
    },
    onError: (error) => {
      toast.error(error.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['getProjectOpenItems', id] });
    },
  });

  const recoverProjectMutation = useMutation({
    mutationFn: () => backend.recoverProject(id!).then((res) => res.data),
    onSuccess: (result) => {
      const recoverToast = formatRecoverProjectToast(result);
      toast.show(recoverToast.message, recoverToast.type);
    },
    onError: (error) => {
      toast.error(error.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['getProject', id] });
      queryClient.invalidateQueries({ queryKey: ['getProjectOpenItems', id] });
    },
  });

  const actionsBusy =
    finalizeProjectMutation.isPending || reclassifyMutation.isPending || recoverProjectMutation.isPending;

  const busyLabel = finalizeProjectMutation.isPending
    ? 'Finalizing…'
    : recoverProjectMutation.isPending
      ? 'Recovering…'
      : reclassifyMutation.isPending
        ? 'Reclassifying…'
        : null;

  const documentCount = documentIds?.length ?? 0;
  const confirmMessage =
    confirmScope === 'all'
      ? `This re-runs classification and extraction for all ${documentCount} document${documentCount === 1 ? '' : 's'} in this project, and cascades to every other project these documents are linked to.`
      : `This re-runs classification and extraction for all ${documentCount} document${documentCount === 1 ? '' : 's'} in this project. Other projects these documents belong to are unaffected.`;

  return (
    <PageHeader
      title={entityViewTitle(id, 'Project View')}
      description="Manage your document classification Project"
      icon={<BeakerIcon className="size-6 " />}
      showBackButton={true}
    >
      <div className="flex md:items-center gap-2 flex-col md:flex-row w-full md:justify-end whitespace-nowrap">
        <DebugObject object={debugObject} />
        {id && (
          <Button
            href={`/projects/${encodeURIComponent(id)}/graph`}
            outline
            title="Explore the entities and documents of this project as a graph"
          >
            <ShareIcon className="size-4" />
            Graph
          </Button>
        )}
        {id && (
          <Button
            href={`/document-pages?projectId=${encodeURIComponent(id)}&status=error`}
            outline
            title="View every page in this project that failed processing"
          >
            <ExclamationTriangleIcon className="size-4" />
            View Failed Pages
          </Button>
        )}
        <ExtractionFreshnessBadge freshness={extractionFreshness} />
        <AuthGuard roles={['admin', 'curator']}>
          <Dropdown>
            <DropdownButton as={Button} outline disabled={actionsBusy}>
              {busyLabel ? (
                <>
                  <FaSpinner className="size-4 animate-spin" />
                  {busyLabel}
                </>
              ) : (
                'Actions'
              )}
              <ChevronDownIcon />
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              <DropdownItem disabled={finalizeBusy} onClick={() => finalizeProjectMutation.mutate()}>
                <CheckCircleIcon />
                <DropdownLabel>Finalize Project</DropdownLabel>
              </DropdownItem>
              {documentIds && (
                <>
                  <DropdownDivider />
                  <DropdownItem
                    onClick={() => recoverProjectMutation.mutate()}
                    title="Re-drive only the stuck or failed pages in this project — safe and targeted, leaves healthy pages untouched"
                  >
                    <WrenchScrewdriverIcon />
                    <DropdownLabel>Recover stuck/failed</DropdownLabel>
                  </DropdownItem>
                  <DropdownDivider />
                  <DropdownItem
                    onClick={() => setConfirmScope('project')}
                    title="Re-runs classification against this project only — other projects these documents belong to are unaffected"
                  >
                    <ArrowPathIcon />
                    <DropdownLabel>Reclassify in this project</DropdownLabel>
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => setConfirmScope('all')}
                    title="Re-runs classification against every project these documents are linked to"
                  >
                    <ArrowPathIcon />
                    <DropdownLabel>Reclassify in all linked projects</DropdownLabel>
                  </DropdownItem>
                </>
              )}
            </DropdownMenu>
          </Dropdown>
        </AuthGuard>
      </div>
      <ConfirmDialog
        open={confirmScope !== null}
        title={confirmScope === 'all' ? 'Reclassify in all linked projects?' : 'Reclassify in this project?'}
        message={confirmMessage}
        confirmLabel="Reclassify"
        pendingLabel="Reclassifying…"
        color="amber"
        isPending={reclassifyMutation.isPending}
        onClose={() => setConfirmScope(null)}
        onConfirm={() => {
          const scope = confirmScope;
          setConfirmScope(null);
          if (scope) {
            reclassifyMutation.mutate(scope);
          }
        }}
      />
    </PageHeader>
  );
}
